import styles from './styles.module.scss';
import { Modal, ModalProps } from 'components-antd';
import { Button } from 'components';
import { useEffect, useState } from 'react';
import moment from 'moment';
import { CloseModalIconSmall } from 'pages/ServicesCategory/components/AddVendorModal/icons';
import Icons from '../../icons';
import { hexToRgba } from 'utils';
import { createPromotionByVendor, updatePromotionById } from 'api/promos';
import { showErrorMessage, showSuccessMessage } from 'helpers';
import { DatePicker } from 'antd';

export interface PromoModalPropsType extends ModalProps {
  handleModalClose: () => void;
  promo: any;
  handlePromoAction: (added: boolean, promo) => void;
}

const PromoModal = (props: PromoModalPropsType) => {
  const { open, handleModalClose, promo, handlePromoAction } = props;
  const [saving, setSaving] = useState<boolean>(false);
  const [promoState, setPromoState] = useState<any>({});
  const defaultBannerColor = '#928CDA';

  useEffect(() => {
    if (promo)
      setPromoState({
        Headline: promo.Headline,
        Description: promo.Description || undefined,
        ExpirationDate: promo.ExpirationDate || undefined,
        BannerColor: promo.BannerColor || defaultBannerColor,
        IsActive: promo.IsActive,
      });
    else
      setPromoState({
        Headline: undefined,
        Description: undefined,
        ExpirationDate: undefined,
        BannerColor: defaultBannerColor,
        IsActive: true,
      });
  }, [promo]);

  const createPromo = async () => {
    setSaving(true);
    try {
      const response = await createPromotionByVendor(promoState);
      if (response.status === 200 && response?.data?.result) {
        handlePromoAction(true, response?.data?.result);
        showSuccessMessage('Promo created');
        handleModalClose();
      }
    } catch (e: any) {
      showErrorMessage(e?.response?.data?.message || 'Unable to create promo.');
    }
    setSaving(false);
  };

  const updatePromo = async () => {
    if (!promo?.Id) return;

    setSaving(true);
    try {
      const response = await updatePromotionById(promo.Id, promoState);
      if (response.status === 200 && response?.data?.result) {
        handlePromoAction(false, response?.data?.result);
        showSuccessMessage('Promo updated');
        handleModalClose();
      }
    } catch (e: any) {
      showErrorMessage(e?.response?.data?.message || 'Unable to update promo.');
    }
    setSaving(false);
  };

  const disabledDate = (current) => {
    return current && current < moment().startOf('day');
  };

  const bannerColors = ['#4673D1', '#FF576D', '#51BFE1', '#928CDA', '#FB913A', '#04A451'];

  return (
    <Modal
      className={styles.promoModal}
      footer={null}
      width={675}
      open={open}
      onCancel={handleModalClose}
      destroyOnClose
      closable={true}
      zIndex={1000}
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <div></div>
          <div>{`${promo ? 'Edit' : 'New'} Promo`}</div>
          <div className={styles.close} onClick={handleModalClose}>
            <CloseModalIconSmall />
          </div>
        </div>
        <div className={styles.details}>
          <div className={styles.box}>
            <div className={styles.label}>Headline</div>
            <input
              type="text"
              className={styles.input}
              value={promoState.Headline || ''}
              onInput={(e: any) => {
                setPromoState((prev) => ({
                  ...prev,
                  Headline: e.target.value,
                }));
              }}
            />
          </div>
          <div className={styles.box}>
            <div className={styles.label}>Description</div>
            <input
              type="text"
              className={styles.input}
              value={promoState.Description || ''}
              onInput={(e: any) => {
                setPromoState((prev) => ({
                  ...prev,
                  Description: e.target.value,
                }));
              }}
            />
          </div>
          <div className={styles.box}>
            <div className={styles.label}>Expiration Date</div>
            <div className={styles.input}>
              <Icons variant={Icons.CALENDAR} />
              <DatePicker
                value={
                  promoState.ExpirationDate
                    ? moment(promoState.ExpirationDate, 'YYYY-MM-DD')
                    : undefined
                }
                onChange={(date, dateString) => {
                  setPromoState((prev) => ({
                    ...prev,
                    ExpirationDate: date ? date.format('YYYY-MM-DD HH:mm:ss.SSS Z') : undefined,
                  }));
                }}
                className={styles.datePicker}
                placeholder={'MM/DD/YYYY'}
                format={'MM/DD/YYYY'}
                bordered={false}
                allowClear={true}
                disabledDate={disabledDate}
              />
            </div>
          </div>
          <div className={styles.box}>
            <div className={styles.label}>Preview</div>
            <div
              className={styles.preview}
              style={{ backgroundColor: hexToRgba(promoState.BannerColor, 0.12) }}
            >
              <div className={styles.previewTitle}>{promoState.Headline || ''}</div>
              {promoState.Description && (
                <div className={styles.previewDate}>{promoState.Description}</div>
              )}
              {promoState.ExpirationDate && (
                <div className={styles.previewDate}>{`Expires on ${moment(
                  promoState.ExpirationDate,
                ).format('MM/DD/YYYY')}`}</div>
              )}
            </div>
            <div className={styles.palette}>
              {bannerColors.map((color) => (
                <div
                  className={styles.colorBox}
                  style={{ borderColor: color === promoState.BannerColor ? color : 'transparent' }}
                  onClick={() => {
                    setPromoState((prev) => ({
                      ...prev,
                      BannerColor: color,
                    }));
                  }}
                >
                  <div
                    className={styles.color}
                    style={{ backgroundColor: hexToRgba(color, 0.4) }}
                  ></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            title="Done"
            isPending={saving}
            loaderClassName={styles.loader}
            onClick={promo?.Id ? updatePromo : createPromo}
            disabled={saving || !promoState.Headline}
            className={styles.btnDone}
          />
        </div>
      </div>
    </Modal>
  );
};

export default PromoModal;
